import React, { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button, useRestoreFocusTarget } from '@fluentui/react-components';
import {
  matchStepState,
  useWizardService,
  STEP_STATES,
  WIZARD_EVENTS,
} from '@bingads-webui-react/wizard-core';

interface NextButtonProps {
  buttonText?: string;
  disabled?: boolean;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
}

export const NextButton: React.FC<NextButtonProps> = ({ buttonText, disabled, appearance }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    send(WIZARD_EVENTS.SAVE_AND_NEXT);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'primary'}
      disabled={disabled || isBlocking}
      onClick={onNextClicked}
      className="step-next-button"
    >
      {buttonText || i18n.getString(_TL_('Next'))}
    </Button>
  );
};

interface BackButtonProps {
  buttonText?: string;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
  icon?: any;
}

export const BackButton: React.FC<BackButtonProps> = ({ buttonText, appearance, icon }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onBackClicked = useCallback(() => {
    send(WIZARD_EVENTS.BACK);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'secondary'}
      icon={icon}
      disabled={isBlocking}
      onClick={onBackClicked}
      className="step-back-button"
    >
      {buttonText || i18n.getString(_TL_('Previous'))}
    </Button>
  );
};

interface SaveAndExitButtonProps {
  buttonText?: string;
  disabled?: boolean;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
}

export const SaveAndExitButton: React.FC<SaveAndExitButtonProps> = ({ buttonText, disabled, appearance }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'primary'}
      disabled={disabled || isBlocking}
      onClick={onNextClicked}
      className="step-save-and-exit-button"
    >
      {buttonText || i18n.getString(_TL_('Save and exit'))}
    </Button>
  );
};

interface BackToSummaryButtonProps {
  buttonText?: string;
  disabled?: boolean;
  summaryStepId: string;
}

export const BackToSummaryButton: React.FC<BackToSummaryButtonProps> = ({ buttonText, disabled, summaryStepId }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onBackToSummaryClicked = useCallback(() => {
    send(WIZARD_EVENTS.SAVE_AND_GOTO, {
      stepId: summaryStepId,
    });
  }, [send, summaryStepId]);

  return (
    <Button
      appearance="primary"
      disabled={disabled || isBlocking}
      onClick={onBackToSummaryClicked}
      className="step-back-to-summary-button"
    >
      {buttonText || i18n.getString(_TL_('Save & return to summary'))}
    </Button>
  );
};

interface CancelButtonProps {
  buttonText?: string;
}

export const CancelButton: React.FC<CancelButtonProps> = ({ buttonText }) => {
  const i18n = useContext(I18nContext) as any;

  const restoreFocusAttribute = useRestoreFocusTarget();

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onCancelClicked = useCallback(() => {
    send(WIZARD_EVENTS.CANCEL);
  }, [send]);

  return (
    <Button
      appearance="subtle"
      disabled={isBlocking}
      onClick={onCancelClicked}
      className="step-cancel-button"
      {...restoreFocusAttribute}
    >
      {buttonText || i18n.getString(_TL_('Cancel'))}
    </Button>
  );
};
